import React from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Chip, Button, Box, Typography, Stack } from "@mui/material";
import {
  Visibility,
  CreditCard,
  AccountBalance,
  QrCode,
} from "@mui/icons-material";
import {
  getPartyEmail,
  getPartyName,
  getPartyPhone,
  getReferenceId,
  isWalletTopup,
} from "../../utils/paymentDisplay";

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "SUCCESS":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
    case "CANCELLED":
      return "error";
    default:
      return "default";
  }
};

const getMethodIcon = (method) => {
  const m = method?.toLowerCase();
  if (m?.includes("upi")) return <QrCode sx={{ mr: 1, fontSize: 18 }} />;
  if (m?.includes("card")) return <CreditCard sx={{ mr: 1, fontSize: 18 }} />;
  return <AccountBalance sx={{ mr: 1, fontSize: 18 }} />;
};

const CustomToolbar = () => (
  <GridToolbarContainer sx={{ p: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
    <GridToolbarQuickFilter
      placeholder="Search Payments..."
      sx={{
        width: { xs: "100%", sm: 300 },
        "& .MuiInputBase-root": {
          borderRadius: "8px",
          bgcolor: "background.paper",
        },
      }}
    />
    <Box>
      <GridToolbarExport
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
        }}
      />
    </Box>
  </GridToolbarContainer>
);

const ModernPaymentTable = ({ data = [], onRowClick, loading }) => {
  const columns = [
    {
      field: "booking_id",
      headerName: "Booking / Dealer ID",
      width: 250,
      valueGetter: (_params, row) => getReferenceId(row),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="primary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "user_name",
      headerName: "Customer / Dealer",
      width: 280,
      valueGetter: (_params, row) => getPartyName(row),
      renderCell: (params) => (
        <Stack
          direction="column"
          spacing={0}
          justifyContent="center"
          height="100%"
        >
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.1 }}>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.1 }}
          >
            {getPartyEmail(params.api.getRow(params.id))}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ lineHeight: 1.1, fontWeight: 500 }}
          >
            {getPartyPhone(params.api.getRow(params.id))}
          </Typography>
          {isWalletTopup(params.api.getRow(params.id)) && (
            <Typography variant="caption" color="success.main" sx={{ lineHeight: 1.1, fontWeight: 700 }}>
              Dealer wallet top-up
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "orderAmount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" fontWeight={700}>
            ₹{params.value?.toLocaleString("en-IN")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "payment_method",
      headerName: "Method",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {getMethodIcon(params.value)}
          <Typography variant="body2">{params.value || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "order_status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={params.value}
            color={getStatusColor(params.value)}
            size="small"
            sx={{
              fontWeight: 700,
              borderRadius: "6px",
              textTransform: "uppercase",
              fontSize: "0.65rem",
            }}
          />
        </Box>
      ),
    },
    {
      field: "transaction_id",
      headerName: "Transaction ID",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 180,
      valueFormatter: (params) =>
        params ? new Date(params).toLocaleString("en-IN") : "N/A",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2">
            {params.value
              ? new Date(params.value).toLocaleString("en-IN")
              : "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(params.row);
            }}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              fontSize: "0.75rem",
            }}
          >
            Details
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: 650,
        width: "100%",
        "& .MuiDataGrid-root": {
          border: "none",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid",
          borderColor: "divider",
        },
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: "#f8fafc",
          borderBottom: "2px solid",
          borderColor: "divider",
        },
      }}
    >
      <DataGrid
        rows={data}
        columns={columns}
        getRowId={(row) => row._id}
        loading={loading}
        rowHeight={85}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        disableRowSelectionOnClick
        onRowClick={(params) => onRowClick(params.row)}
      />
    </Box>
  );
};

export default ModernPaymentTable;
